/**
 * React hook that polls the backend sync-service status every 30s.
 * Provides cloud sync health for the SyncStatusIndicator component.
 * Falls back gracefully when the sync-service is unreachable (not deployed).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import syncService, { SyncStatus } from '../services/syncService';

const POLL_INTERVAL_MS = 30_000;

export interface UseSyncStatusResult {
  /** Full sync status object, null until first successful fetch */
  status: SyncStatus | null;
  /** Whether the sync-service is reachable */
  available: boolean;
  /** Loading state for manual trigger */
  triggering: boolean;
  /** Force an immediate push+pull cycle */
  triggerSync: () => Promise<void>;
  /** Refresh status immediately */
  refresh: () => Promise<void>;
}

export function useSyncStatus(): UseSyncStatusResult {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [available, setAvailable] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await syncService.getStatus();
      setStatus(data);
      setAvailable(true);
    } catch {
      // Sync service not running or not reachable — hide indicator
      setAvailable(false);
      setStatus(null);
    }
  }, []);

  const triggerSync = useCallback(async () => {
    setTriggering(true);
    try {
      await syncService.trigger();
      // Refresh status after trigger
      await fetchStatus();
    } catch {
      // ignore — status will update on next poll
    } finally {
      setTriggering(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchStatus]);

  return { status, available, triggering, triggerSync, refresh: fetchStatus };
}
